import { type FormEvent, type ReactNode, useCallback, useEffect, useRef } from 'react';
import { t } from '../../i18n';
import { isTopDialogLayer, popDialogLayer, pushDialogLayer } from './dialogStack';

/**
 * The bottom sheet, written once.
 *
 * Eleven files had their own `<div className="modal-backdrop"><div
 * className="modal-panel">`, which meant eleven chances to forget the tall
 * variant, and eleven sheets that ignored the Escape key because only
 * Modal.tsx ever bothered to listen for it.
 *
 * Dismissing on a backdrop tap is opt-in, not the default: most of these
 * sheets are forms with a loan amount or a month of hours typed into them,
 * and a stray thumb on the dark area beside a phone-width panel should not
 * throw that away. The two dialogs that carry nothing (confirm, prompt) ask
 * for it explicitly.
 */
export function Sheet({
  title,
  children,
  onClose,
  onSubmit,
  tall = false,
  gap = 4,
  closeOnBackdrop = false,
  showClose = false,
  titleId,
}: {
  title?: ReactNode;
  children: ReactNode;
  onClose: () => void;
  /** Passing this renders the panel as a <form> and wires submit. */
  onSubmit?: (e: FormEvent) => void;
  /** Caps the panel at 90vh and scrolls inside it — for the long forms. */
  tall?: boolean;
  /** Vertical rhythm between children; 4 unless the sheet says otherwise. */
  gap?: 4 | 5;
  closeOnBackdrop?: boolean;
  showClose?: boolean;
  titleId?: string;
}) {
  useEscape(onClose);

  const panelRef = useRef<HTMLElement | null>(null);
  useModalFocus(panelRef);

  const panel = [
    'modal-panel',
    gap === 5 ? 'space-y-5' : 'space-y-4',
    tall ? 'max-h-[90vh] overflow-y-auto' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const head = title ? (
    <header className={showClose ? 'flex items-center justify-between' : undefined}>
      <h2 className="font-heading font-bold tracking-tight text-2xl" id={titleId}>
        {title}
      </h2>
      {showClose && (
        <button
          type="button"
          onClick={onClose}
          className="-m-2 p-3 text-2xl text-muted-foreground hover:text-foreground leading-none"
          aria-label={t('Close')}
        >
          ×
        </button>
      )}
    </header>
  ) : null;

  const body = (
    <>
      {head}
      {children}
    </>
  );

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the backdrop click is a redundant convenience for pointer users only — keyboard users already close this sheet with Escape via useEscape(onClose) above, and the backdrop itself is never focusable.
    <div
      className="modal-backdrop"
      // biome-ignore lint/a11y/useSemanticElements: a native <dialog> would have to be driven imperatively by showModal()/close(), which changes how every caller mounts and unmounts this sheet; role="dialog" + aria-modal announces identically and keeps the declarative render.
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-label={!titleId && typeof title === 'string' ? title : undefined}
      onClick={closeOnBackdrop ? (e) => e.target === e.currentTarget && onClose() : undefined}
    >
      {onSubmit ? (
        <form
          ref={(el) => {
            panelRef.current = el;
          }}
          onSubmit={onSubmit}
          className={panel}
          tabIndex={-1}
        >
          {body}
        </form>
      ) : (
        <div
          ref={(el) => {
            panelRef.current = el;
          }}
          className={panel}
          tabIndex={-1}
        >
          {body}
        </div>
      )}
    </div>
  );
}

/**
 * Escape closes — the sheet on top, and only that one.
 *
 * Exported because Composer and StaffTour draw their own backdrop rather than
 * using Sheet, and calling this is also how they join the dialog stack, which
 * is what silences the keyboard shortcuts while they are open.
 */
export function useEscape(handler: () => void) {
  const layer = useDialogLayer();
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Every mounted sheet hears this keypress; without the stack check the
      // one behind closed at the same moment as the one in front.
      if (e.key !== 'Escape' || !layer()) return;
      handler();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handler, layer]);
}

/** Registers this dialog for the lifetime of the component; call the returned
 *  predicate to ask whether it is currently the topmost one. */
function useDialogLayer(): () => boolean {
  const id = useRef(0);
  useEffect(() => {
    id.current = pushDialogLayer();
    return () => popDialogLayer(id.current);
  }, []);
  return useCallback(() => isTopDialogLayer(id.current), []);
}

/**
 * Focus goes into the panel, stays there, and comes back out where it started.
 *
 * The sheet announced itself as `aria-modal` and behaved like nothing of the
 * sort: Tab walked straight off the last field into the page underneath, and
 * a screen reader could keep reading a form the person could no longer see.
 * Two halves fix that — a Tab that wraps inside the panel, and everything
 * outside the panel marked `inert` so it is not reachable by any route at all.
 */
export function useModalFocus(panelRef: React.MutableRefObject<HTMLElement | null>) {
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const previous = document.activeElement as HTMLElement | null;
    panel.focus();
    const restore = inertOutside(panel);

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab' || !panel) return;
      // A sheet opened on top of this one traps first (its panel is a DOM
      // descendant, so its handler runs before this one on the way up) and
      // marks the wrap it made; this sheet must not wrap a second time.
      if (e.defaultPrevented) return;
      const items = focusable(panel);
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0] as HTMLElement;
      const last = items[items.length - 1] as HTMLElement;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    panel.addEventListener('keydown', onKeyDown);
    return () => {
      panel.removeEventListener('keydown', onKeyDown);
      restore();
      previous?.focus();
    };
  }, [panelRef]);
}

const FOCUSABLE =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

function focusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      el.tabIndex >= 0 &&
      // Anything a nested sheet has already switched off, plus the hidden
      // halves of things like a collapsed picker.
      !el.closest('[inert]') &&
      el.getClientRects().length > 0,
  );
}

/**
 * Everything that is not this panel stops existing while it is open.
 *
 * Walks up to <body> switching off each ancestor's other children, which is
 * the only way to reach "the rest of the app" from a sheet that renders inline
 * in the tree rather than through a portal. Nesting works out on its own: a
 * second sheet's own walk switches off the first, and each walk only restores
 * the elements it set, so unwinding in either order is safe.
 */
function inertOutside(el: Element): () => void {
  const marked: Element[] = [];
  let node: Element = el;
  // Stop below <html>: <head> is not part of "the rest of the app".
  while (node.parentElement && node.parentElement !== document.documentElement) {
    const parent: Element = node.parentElement;
    const siblings: Element[] = Array.from(parent.children);
    for (const sibling of siblings) {
      if (sibling === node) continue;
      if (sibling.tagName === 'SCRIPT' || sibling.tagName === 'STYLE') continue;
      // The toast layer is how this app reports that the save inside the
      // sheet failed. Muting it would mute the sheet's own error messages.
      if (sibling.hasAttribute('aria-live') || sibling.hasAttribute('data-sonner-toaster'))
        continue;
      if (sibling.hasAttribute('inert')) continue;
      sibling.setAttribute('inert', '');
      marked.push(sibling);
    }
    node = parent;
  }
  return () => {
    for (const m of marked) m.removeAttribute('inert');
  };
}
